import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  Settings, 
  Users,
  Eye,
  EyeOff,
  School,
  Bell,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  SchoolAdminsTab, 
  SchoolInfoTab, 
  EmailPreferencesTab, 
  SecurityTab 
} from "../components/school/tabs";

const PERMISSION_OPTIONS = [
  { id: "manage_students", label: "Manage Students", description: "Add, edit, remove students" },
  { id: "bulk_upload", label: "Bulk Upload", description: "Upload students via CSV" },
  { id: "manage_teachers", label: "Manage Teachers", description: "Add, edit, remove teachers" },
  { id: "view_billing", label: "View Billing", description: "View invoices and billing summary" },
  { id: "manage_billing", label: "Manage Billing", description: "Start/modify subscriptions" },
  { id: "view_analytics", label: "View Analytics", description: "View performance analytics" },
  { id: "export_data", label: "Export Data", description: "Export reports and data" },
  { id: "manage_admins", label: "Manage Admins", description: "Add, edit other admins" },
];

const ROLE_OPTIONS = [
  { value: "school_admin", label: "Admin" },
  { value: "school_coordinator", label: "Coordinator" },
  { value: "school_viewer", label: "Viewer" },
];

export default function SchoolSettings() {
  const { admin, school, refreshSchool } = useOutletContext();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  
  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    role: "school_admin",
    permissions: ["manage_students", "view_analytics"]
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    billing_reminders: true,
    payment_alerts: true,
    student_activity: false,
    admin_activity: true,
    weekly_digest: true
  });
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const permissions = admin?.permissions || [];
  const canManageAdmins = permissions.includes("manage_admins");

  useEffect(() => {
    if (canManageAdmins) {
      fetchAdmins();
    } else {
      setLoading(false);
    }
    fetchEmailPreferences();
  }, [canManageAdmins]);

  const fetchEmailPreferences = async () => {
    try {
      const response = await fetch(`${API}/school/notifications/email-preferences`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences) {
          setEmailPreferences(data.preferences);
        }
      }
    } catch (error) {
      console.error("Error fetching email preferences:", error);
    }
  };

  const updateEmailPreference = async (key, value) => {
    const newPrefs = { ...emailPreferences, [key]: value };
    setEmailPreferences(newPrefs);
    setSavingEmailPrefs(true);
    
    try {
      const response = await fetch(`${API}/school/notifications/email-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [key]: value }),
      });
      
      if (response.ok) {
        toast.success("Email preferences updated");
      } else {
        toast.error("Failed to update preferences");
        setEmailPreferences(emailPreferences); // Revert
      }
    } catch (error) {
      toast.error("Network error");
      setEmailPreferences(emailPreferences); // Revert
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API}/school/notifications/test-email`, {
        method: "POST",
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Test email sent successfully");
      } else {
        toast.error(data.detail || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/school/admin/list`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || []);
      } else {
        toast.error("Failed to fetch admins");
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.name || !newAdmin.email) {
      toast.error("Name and Email are required");
      return;
    }

    try {
      const response = await fetch(`${API}/school/admin/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newAdmin),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Admin added successfully");
        setShowAddModal(false);
        setNewAdmin({
          name: "",
          email: "",
          phone: "",
          designation: "",
          role: "school_admin",
          permissions: ["manage_students", "view_analytics"]
        });
        fetchAdmins();
      } else {
        toast.error(data.detail || data.error || "Failed to add admin");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleUpdateAdmin = async (adminId, updates) => {
    try {
      const response = await fetch(`${API}/school/admin/${adminId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Admin updated successfully");
        setEditingAdmin(null);
        fetchAdmins();
      } else {
        toast.error(data.detail || data.error || "Failed to update admin");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    try {
      const response = await fetch(`${API}/school/admin/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Password changed successfully");
        setShowPasswordModal(false);
        setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      } else {
        toast.error(data.detail || data.error || "Failed to change password");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const togglePermission = (perm) => {
    if (editingAdmin) {
      const current = editingAdmin.permissions || [];
      const updated = current.includes(perm)
        ? current.filter(p => p !== perm)
        : [...current, perm];
      setEditingAdmin({ ...editingAdmin, permissions: updated });
    } else {
      const current = newAdmin.permissions || [];
      const updated = current.includes(perm)
        ? current.filter(p => p !== perm)
        : [...current, perm];
      setNewAdmin({ ...newAdmin, permissions: updated });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500 text-sm">Manage school settings and administrators</p>
      </div>

      <Tabs defaultValue="admins" className="space-y-6">
        <TabsList className="bg-white border shadow-sm flex-wrap">
          <TabsTrigger value="admins" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            <Users className="w-4 h-4 mr-2" />
            Administrators
          </TabsTrigger>
          <TabsTrigger value="school" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            <School className="w-4 h-4 mr-2" />
            School Info
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            <Bell className="w-4 h-4 mr-2" />
            Email Notifications
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Administrators Tab */}
        <TabsContent value="admins" className="space-y-6">
          <SchoolAdminsTab
            admins={admins}
            currentAdmin={admin}
            loading={loading}
            canManageAdmins={canManageAdmins}
            onAddAdmin={() => setShowAddModal(true)}
            onEditAdmin={setEditingAdmin}
            onUpdateAdmin={handleUpdateAdmin}
          />
        </TabsContent>

        {/* School Info Tab */}
        <TabsContent value="school" className="space-y-6">
          <SchoolInfoTab school={school} />
        </TabsContent>

        {/* Email Notifications Tab */}
        <TabsContent value="email" className="space-y-6">
          <EmailPreferencesTab
            admin={admin}
            emailPreferences={emailPreferences}
            savingEmailPrefs={savingEmailPrefs}
            sendingTestEmail={sendingTestEmail}
            onUpdatePreference={updateEmailPreference}
            onSendTestEmail={sendTestEmail}
          />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <SecurityTab
            permissions={permissions}
            onChangePassword={() => setShowPasswordModal(true)}
          />
        </TabsContent>
      </Tabs>

      {/* Add Admin Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Administrator</DialogTitle>
            <DialogDescription>
              Add a new admin to manage the school portal
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1"
                  data-testid="new-admin-name"
                />
              </div>
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1"
                  data-testid="new-admin-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newAdmin.phone}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, phone: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input
                  value={newAdmin.designation}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g., Vice Principal"
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Role</Label>
                <Select 
                  value={newAdmin.role} 
                  onValueChange={(val) => setNewAdmin(prev => ({ ...prev, role: val }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-3 block">Permissions</Label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {PERMISSION_OPTIONS.map(perm => (
                  <div 
                    key={perm.id} 
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded"
                  >
                    <div>
                      <p className="text-sm font-medium">{perm.label}</p>
                      <p className="text-xs text-slate-500">{perm.description}</p>
                    </div>
                    <Switch
                      checked={newAdmin.permissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-add-admin">
                Add Administrator
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Permissions Modal */}
      <Dialog open={!!editingAdmin} onOpenChange={() => setEditingAdmin(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Permissions</DialogTitle>
            <DialogDescription>
              Update permissions for {editingAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3">
              {PERMISSION_OPTIONS.map(perm => (
                <div 
                  key={perm.id} 
                  className="flex items-center justify-between p-2 hover:bg-slate-50 rounded"
                >
                  <div>
                    <p className="text-sm font-medium">{perm.label}</p>
                    <p className="text-xs text-slate-500">{perm.description}</p>
                  </div>
                  <Switch
                    checked={(editingAdmin?.permissions || []).includes(perm.id)}
                    onCheckedChange={() => togglePermission(perm.id)}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAdmin(null)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleUpdateAdmin(editingAdmin.id, { permissions: editingAdmin.permissions })}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-password-change">
                Update Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
